import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr, caClient, setCaClient } from "@/lib/api";
import { AuthContext, useAuth } from "@/context/AuthContext";
import ClientRequestsTab from "@/components/ca/ClientRequestsTab";
import { hasPerm } from "@/lib/perms";
import { PageHeader } from "@/components/pawkt/PageHeader";
import VendorsTab from "@/components/accounts/VendorsTab";
import PurchasesTab from "@/components/accounts/PurchasesTab";
import BillsTab from "@/components/accounts/BillsTab";
import PaymentsTab from "@/components/accounts/PaymentsTab";
import CasesTab, { Overview, SettingsTab } from "@/components/accounts/CasesTab";
import MatchingTab from "@/components/accounts/MatchingTab";
import ClosingTab from "@/components/accounts/ClosingTab";
import { MoneyIn, BankMatching, VendorStatements, MonthEnd } from "@/components/accounts/MoneyTabs";
import InvoiceRegister from "@/components/accounts/InvoiceRegister";
import MoneyOutPanel from "@/components/accounts/MoneyOutPanel";
import RecordPaymentButton from "@/components/accounts/RecordPayment";

// Each view keeps its own permission; groups only decide where it appears. ?tab=<view key> deep-links.
const VIEWS = [
  { key: "money_in", label: "Receipts & collections", perm: "payables.reconcile" },
  { key: "invoices", label: "All invoices & collections", perm: "payables.reconcile" },
  { key: "money_out", label: "Outgoing payments", perm: "payables.view" },
  { key: "overview", label: "Overview", perm: "payables.view" },
  { key: "purchases", label: "Purchase requests", perm: "purchases.view" },
  { key: "vendors", label: "Vendors & bank details", perm: "vendors.view" },
  { key: "bills", label: "Vendor bills", perm: "payables.view" },
  { key: "payments", label: "Payments", perm: "payables.view" },
  { key: "cases", label: "Missing documents & exceptions", perm: "payables.view" },
  { key: "bank", label: "Bank Matching", perm: "payables.reconcile", noCa: true },
  { key: "statements", label: "Vendor statements", perm: "payables.view" },
  { key: "matching", label: "Statement matching", perm: "payables.view" },
  { key: "month_end", label: "Month-end review", perm: "payables.view" },
  { key: "closing", label: "Year-end readiness", perm: "payables.view" },
  { key: "settings", label: "Settings & lock", perm: "payables.view" },
  { key: "ca_requests", label: "CA requests", perm: "payables.view", own: true },
];
const GROUPS = [
  { key: "in", label: "Money In", sub: "Customer receipts", views: ["money_in", "invoices"] },
  { key: "out", label: "Money Out", sub: "Purchases & spend", views: ["money_out", "overview", "purchases"] },
  { key: "vendor", label: "Vendor Bills & Payments", sub: "Vendors, bills, payments, documents", views: ["vendors", "bills", "payments", "cases"] },
  { key: "bank", label: "Bank Matching", sub: "Match bank lines", views: ["bank"] },
  { key: "reports", label: "Reports", sub: "Statements & month-end", views: ["statements", "matching", "month_end"], advanced: true },
  { key: "more", label: "More", sub: "Year-end, settings, CA", views: ["closing", "settings", "ca_requests"], advanced: true },
];
// Reports/More are Accounts & Finance tools; hidden (not blocked) for Procurement.
const seesAdvanced = (u) => hasPerm(u, "payables.verify") || hasPerm(u, "payables.ca_review") || ["owner", "admin"].includes(u?.role);

function CaBanner({ ctx, onExit }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm" data-testid="ca-client-banner">
      <span className="min-w-0 flex-1"><b>CA client mode</b> · {ctx.client_name} · {ctx.ca_role.replace("_", " ")} · scope: {ctx.scope}{ctx.branch_restricted ? ` · branches: ${ctx.branches.map((b) => b.name).join(", ")}` : ""}
        <span className="block text-xs text-indigo-700">Review access only: you can't execute or confirm payments, verify bills or post entries.</span></span>
      <button className="rounded-md border border-indigo-300 bg-white px-3 py-1 text-xs font-medium" onClick={onExit} data-testid="ca-exit-client">Exit client</button>
    </div>
  );
}

export default function Accounts() {
  const auth = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [ca] = useState(caClient());
  const { data: ctx, error } = useQuery({ queryKey: ["ca-client-ctx", ca], enabled: !!ca, retry: false, queryFn: async () => (await api.get("/ca/client/context")).data });
  const exit = () => { setCaClient(""); qc.clear(); nav("/ca"); };
  useEffect(() => {
    const h = () => { toast.error("Your firm's access to this client is no longer active"); qc.clear(); nav("/ca"); };
    window.addEventListener("ca-access-denied", h);
    return () => window.removeEventListener("ca-access-denied", h);
  }, [qc, nav]);
  if (!ca) return <AccountsBody />;
  if (error) return <p className="text-sm text-rose-600" data-testid="ca-ctx-error">{apiErr(error)}</p>;
  if (!ctx) return <p className="text-sm text-slate-500">Opening client…</p>;
  const user = { ...ctx.user, effective_permissions: ctx.permissions, ca_ctx: ctx };
  return (
    <AuthContext.Provider value={{ ...auth, user }}>
      <CaBanner ctx={ctx} onExit={exit} />
      <AccountsBody key={ca} />
    </AuthContext.Provider>
  );
}

function AccountsBody() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const ok = (v) => hasPerm(user, v.perm) && !(v.own && user?.ca_ctx) && !(v.noCa && user?.ca_ctx);
  const views = VIEWS.filter(ok);
  const groups = GROUPS.filter((g) => !g.advanced || seesAdvanced(user)).map((g) => ({ ...g, views: g.views.filter((k) => views.some((v) => v.key === k)) })).filter((g) => g.views.length);
  const alias = { month: "month_end" }[params.get("tab")] || params.get("tab");
  const start = groups.flatMap((g) => g.views).includes(alias) ? alias : groups[0]?.views[0];
  const [tab, setTab] = useState(start);
  if (!groups.length) {
    return <div className="rounded-2xl border border-pawkt-border bg-white p-8 text-center" data-testid="no-access"><p className="font-display text-lg font-bold">You don't have access to Accounts & Purchase</p><p className="mt-1 text-sm text-slate-500">Ask an owner to grant a Purchase, Accounts, Finance Manager or Internal Auditor — Read Only role.</p></div>;
  }
  const cur = groups.find((g) => g.views.includes(tab)) || groups[0];
  const Body = { money_in: () => <MoneyIn goBank={() => setTab("bank")} />, invoices: InvoiceRegister, money_out: MoneyOutPanel, overview: () => <Overview onCases={() => setTab("cases")} />, vendors: VendorsTab, purchases: PurchasesTab, bills: BillsTab, payments: PaymentsTab,
    cases: CasesTab, bank: BankMatching, statements: VendorStatements, matching: MatchingTab, month_end: MonthEnd, closing: ClosingTab, settings: SettingsTab, ca_requests: ClientRequestsTab }[tab] || (() => null);
  return (
    <div data-testid="accounts-page">
      <PageHeader title="Accounts & Purchase" subtitle="Money in, money out, vendor bills and bank matching"><RecordPaymentButton /></PageHeader>
      <div className="mb-4 flex gap-2 overflow-x-auto pb-1" role="tablist" data-testid="accounts-sections">
        {groups.map((g) => (
          <button key={g.key} role="tab" aria-selected={cur.key === g.key} onClick={() => setTab(g.views[0])} data-testid={`accounts-section-${g.key}`}
            className={`min-w-[140px] shrink-0 rounded-xl border px-3 py-2 text-left transition-colors ${cur.key === g.key ? "border-slate-900 bg-slate-900 text-white" : g.advanced ? "border-dashed border-slate-300 bg-slate-50 hover:bg-white" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
            <span className="block text-sm font-semibold">{g.label}</span><span className={`block text-[11px] ${cur.key === g.key ? "text-slate-300" : "text-slate-500"}`}>{g.sub}</span></button>
        ))}
      </div>
      {cur.views.length > 1 && <div className="mb-4 flex gap-1 overflow-x-auto border-b border-slate-200" role="tablist">
        {cur.views.map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`accounts-tab-${k}`}
            className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{VIEWS.find((v) => v.key === k).label}</button>
        ))}
      </div>}
      <Body key={tab} />
    </div>
  );
}
